import { Injectable } from '@angular/core'
import { _objectEntries, IsoDate, localDate, StringMap } from '@naturalcycles/js-lib'
import {
  DailyEntryBM,
  Goal,
  OvulationStatus,
  UFColor,
  UFColorCode,
  UserFertility,
} from '@naturalcycles/shared'
import {
  OuraSyncABTestOvulationStatus,
  ourSyncABTestOvulationStatusMap,
} from './ouraSyncABTest.service'

@Injectable({ providedIn: 'root' })
export class OuraSyncABTestMockService {
  public getMockUfForOuraInsights(): UserFertility | void {
    const promptText = `Type the number of the option you want to choose:\n\n${_objectEntries(
      ourSyncABTestOvulationStatusMap,
    )
      .map(([k, v]) => `${k}: ${v}`)
      .join('\n')}`

    const option = parseInt(prompt(promptText) || '')

    if (
      isNaN(option) ||
      !option ||
      !Object.keys(OuraSyncABTestOvulationStatus).includes(option.toString())
    ) {
      return alert('Invalid option')
    }

    return this.getUfMockByPredictionResult(option as OuraSyncABTestOvulationStatus)
  }

  private getRandomTemperature(): number {
    return Math.random() + 36
  }

  private getMockEntryMap(startDate: IsoDate, numberOfEntries: number): StringMap<DailyEntryBM> {
    const entries: StringMap<DailyEntryBM> = {}
    const date = localDate(startDate)
    for (let i = 0; i < numberOfEntries; i++) {
      entries[date.plusDays(i).toISODate()] = {
        temperature: this.getRandomTemperature(),
        date: date.plusDays(i).toISODate(),
      } as DailyEntryBM
    }
    return entries
  }

  private getMockColorMap(
    startDate: IsoDate,
    numberOfEntries: number,
    ovulationDay?: number,
    ovulationStatus?: OvulationStatus,
  ): StringMap<UFColorCode> {
    const entries: StringMap<UFColorCode> = {}
    const date = localDate(startDate)
    for (let i = 0; i < numberOfEntries; i++) {
      entries[date.plusDays(i).toISODate()] = {
        ovulationStatus: i === ovulationDay ? ovulationStatus : undefined,
        code: {},
        nCycle: 0,
        cd: i,
        color: ovulationDay ? (i > ovulationDay + 1 ? UFColor.GREEN : UFColor.RED) : UFColor.RED,
        date: date.plusDays(i).toISODate(),
        goal: Goal.PREVENT,
      } as UFColorCode
    }
    return entries
  }

  private getUfMockByPredictionResult(option: OuraSyncABTestOvulationStatus): UserFertility {
    const startDate = localDate.today().minusDays(20).toISODate()
    const numberOfEntries = 15

    const options: Record<OuraSyncABTestOvulationStatus, UserFertility> = {
      [OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_RANGE]: {
        ncycles: 1,
        ovulationWindowMax: [6],
        ovulationWindowMin: [4],
        entryMap: this.getMockEntryMap(startDate, numberOfEntries),
        colorMap: this.getMockColorMap(
          startDate,
          numberOfEntries,
          5,
          OvulationStatus.OVU_CONFIRMED,
        ),
      } as UserFertility,
      [OuraSyncABTestOvulationStatus.OVUL_SPECIFIC_DATE]: {
        ncycles: 1,
        ovulationWindowMax: [4],
        ovulationWindowMin: [4],
        entryMap: this.getMockEntryMap(startDate, numberOfEntries),
        colorMap: this.getMockColorMap(
          startDate,
          numberOfEntries,
          4,
          OvulationStatus.OVU_CONFIRMED,
        ),
      } as UserFertility,
      [OuraSyncABTestOvulationStatus.DID_NOT_OVULATE]: {
        ncycles: 1,
        ovulationWindowMax: [4],
        ovulationWindowMin: [4],
        entryMap: this.getMockEntryMap(startDate, numberOfEntries),
        colorMap: this.getMockColorMap(startDate, numberOfEntries, 4, OvulationStatus.ANOVULATORY),
      } as UserFertility,
      [OuraSyncABTestOvulationStatus.CAN_NOT_CONFIRM]: {
        ncycles: 1,
        ovulationWindowMax: [4],
        ovulationWindowMin: [4],
        entryMap: this.getMockEntryMap(startDate, numberOfEntries),
        colorMap: this.getMockColorMap(
          startDate,
          numberOfEntries,
          4,
          OvulationStatus.OVU_NOT_CONFIRMED,
        ),
      } as UserFertility,
      [OuraSyncABTestOvulationStatus.UNKNOWN]: {
        ncycles: 1,
        ovulationWindowMax: [4],
        ovulationWindowMin: [4],
        entryMap: this.getMockEntryMap(startDate, numberOfEntries),
        colorMap: this.getMockColorMap(startDate, numberOfEntries),
      } as UserFertility,
    }

    return options[option]
  }
}
